import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Theme,
  TextField,
  Paper,
  List,
  ListItem,
  InputAdornment,
  ClickAwayListener,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  Fade,
  Grow,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import axios from "axios";
import { FamilyMember } from "types";
import debounce from "lodash/debounce";

const API_URL = "https://sheltered-ridge-72729.herokuapp.com/families";

// Detect if text contains Arabic characters
const isArabicText = (text: string): boolean => {
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  return arabicRegex.test(text);
};

const useStyles = makeStyles((theme: Theme) => ({
  // Desktop: Fixed position on left side
  searchContainer: {
    position: "fixed",
    top: 20,
    left: 20,
    zIndex: 2000000,
    [theme.breakpoints.down("sm")]: {
      top: 16,
      left: 16,
    },
  },
  
  // Search icon button - modern floating style
  searchButton: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    color: "white",
    width: 48,
    height: 48,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
      transform: "scale(1.05)",
    },
  },
  
  // Desktop expanded search - glassmorphism
  desktopExpanded: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    backdropFilter: "blur(20px)",
    borderRadius: 16,
    border: "1px solid rgba(255, 255, 255, 0.2)",
    overflow: "hidden",
    width: 380,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
  },
  
  searchInputWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
  },
  
  desktopInput: {
    flex: 1,
    "& .MuiInputBase-root": {
      fontSize: 15,
      color: "white",
      fontWeight: 500,
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.6)",
      opacity: 1,
    },
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:before": {
      borderBottom: "none !important",
    },
  },
  
  searchIconInput: {
    color: "rgba(255, 255, 255, 0.7)",
    marginRight: 8,
  },
  
  closeButton: {
    color: "rgba(255, 255, 255, 0.7)",
    padding: 6,
    "&:hover": {
      color: "white",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  
  // Results container
  resultsContainer: {
    maxHeight: 400,
    overflow: "auto",
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255, 255, 255, 0.3)",
      borderRadius: 3,
    },
  },
  
  resultItem: {
    cursor: "pointer",
    padding: "12px 16px",
    transition: "background-color 0.2s ease",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  
  resultContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  
  resultIcon: {
    color: "rgba(255, 255, 255, 0.5)",
    marginTop: 2,
    fontSize: 20,
  },
  
  resultText: {
    flex: 1,
    minWidth: 0,
  },
  
  resultName: {
    color: "white",
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  
  resultLineage: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  
  noResults: {
    padding: "24px 16px",
    textAlign: "center",
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: 14,
  },
  
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: 24,
  },
  
  loadingSpinner: {
    color: "rgba(255, 255, 255, 0.7)",
  },
  
  highlight: {
    backgroundColor: "rgba(255, 215, 0, 0.4)",
    color: "white",
    padding: "0 2px",
    borderRadius: 2,
  },
  
  resultCount: {
    padding: "8px 16px",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },

  // Mobile full-page overlay
  mobileOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.98)",
    zIndex: 3000000,
    display: "flex",
    flexDirection: "column",
  },
  
  mobileHeader: {
    display: "flex",
    alignItems: "center",
    padding: 16,
    gap: 12,
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  },
  
  mobileInputWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 12,
    padding: "8px 16px",
  },
  
  mobileInput: {
    flex: 1,
    "& .MuiInputBase-root": {
      fontSize: 16,
      color: "white",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.5)",
      opacity: 1,
    },
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:before": {
      borderBottom: "none !important",
    },
  },
  
  mobileCloseButton: {
    color: "white",
    padding: 8,
  },
  
  mobileResults: {
    flex: 1,
    overflow: "auto",
  },
}));

type SearchResult = FamilyMember;

interface Props {
  isArabic: boolean;
}

const SearchBar: React.FC<Props> = ({ isArabic }) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Auto-detect if query is Arabic
  const isQueryArabic = isArabicText(query);

  const searchMembers = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const searchParts = searchQuery.trim().split(/\s+/);
      
      // Search for EACH word in the query to get comprehensive results
      // This ensures "Hafez Abdel Qader" searches for all three words
      const requests: Promise<any>[] = [];
      
      // Limit to first 3 words to avoid too many API calls
      const searchTerms = searchParts.slice(0, 3);
      
      for (const term of searchTerms) {
        requests.push(
          axios.get(API_URL, { params: { name_contains: term, _limit: 100 } }),
          axios.get(API_URL, { params: { arabicName_contains: term, _limit: 100 } }),
          axios.get(API_URL, { params: { lineage_contains: term, _limit: 100 } }),
          axios.get(API_URL, { params: { arabicLineage_contains: term, _limit: 100 } }),
        );
      }

      const responses = await Promise.all(requests);

      // Merge and dedupe all results
      const memberMap = new Map<number, FamilyMember>();
      responses.forEach((res) => {
        if (res.data && Array.isArray(res.data)) {
          res.data.forEach((m: FamilyMember) => {
            if (m && m.id && !memberMap.has(m.id)) {
              memberMap.set(m.id, m);
            }
          });
        }
      });
      
      // Filter: check if query matches name+lineage
      const members = Array.from(memberMap.values()).filter((member) => {
        const englishFull = `${member.name || ""} ${member.lineage || ""}`.toLowerCase();
        const arabicFull = `${member.arabicName || ""} ${member.arabicLineage || ""}`.toLowerCase();
        
        // Check both language combinations
        const searchLower = searchParts.map(p => p.toLowerCase());
        
        return [englishFull, arabicFull].some((fullName) => {
          let searchStr = fullName;
          for (const part of searchLower) {
            const idx = searchStr.indexOf(part);
            if (idx === -1) return false;
            searchStr = searchStr.slice(idx + part.length);
          }
          return true;
        });
      });

      setResults(members.slice(0, 100));
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((q: string) => searchMembers(q), 300),
    []
  );

  useEffect(() => {
    debouncedSearch(query);
    return () => debouncedSearch.cancel();
  }, [query, debouncedSearch]);

  const handleResultClick = (memberId: number) => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    history.push(`/tree/${memberId}`);
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
  };

  // Get display name based on query language (auto-detect)
  const getDisplayName = (member: FamilyMember) => {
    if (isQueryArabic) {
      return member.arabicName || member.name || "";
    }
    return member.name || member.arabicName || "";
  };

  // Get display lineage based on query language (auto-detect)
  const getDisplayLineage = (member: FamilyMember) => {
    if (isQueryArabic) {
      return member.arabicLineage || member.lineage || "";
    }
    return member.lineage || member.arabicLineage || "";
  };

  const highlightMatch = (text: string, searchQuery: string) => {
    if (!text || !searchQuery) return text;
    
    const searchParts = searchQuery.trim().split(/\s+/);
    let result: (string | JSX.Element)[] = [text];
    
    searchParts.forEach((part, partIndex) => {
      const newResult: (string | JSX.Element)[] = [];
      result.forEach((segment, segIndex) => {
        if (typeof segment === "string") {
          const regex = new RegExp(`(${part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi");
          const parts = segment.split(regex);
          parts.forEach((p, i) => {
            if (p.toLowerCase() === part.toLowerCase()) {
              newResult.push(
                <span key={`${partIndex}-${segIndex}-${i}`} className={classes.highlight}>
                  {p}
                </span>
              );
            } else if (p) {
              newResult.push(p);
            }
          });
        } else {
          newResult.push(segment);
        }
      });
      result = newResult;
    });
    
    return <>{result}</>;
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} className={classes.loadingSpinner} />
        </div>
      );
    }
    
    if (results.length > 0) {
      return (
        <>
          <div className={classes.resultCount}>
            {results.length} {isQueryArabic ? "نتيجة" : "results"}
          </div>
          <List disablePadding>
            {results.map((result, index) => (
              <Fade in timeout={150 + index * 30} key={result.id}>
                <ListItem
                  className={classes.resultItem}
                  onClick={() => handleResultClick(result.id)}
                  disableGutters
                >
                  <div className={classes.resultContent}>
                    <PersonIcon className={classes.resultIcon} />
                    <div className={classes.resultText}>
                      <div 
                        className={classes.resultName}
                        style={{ direction: isQueryArabic ? "rtl" : "ltr" }}
                      >
                        {highlightMatch(getDisplayName(result), query)}
                      </div>
                      <div 
                        className={classes.resultLineage}
                        style={{ direction: isQueryArabic ? "rtl" : "ltr" }}
                      >
                        {highlightMatch(getDisplayLineage(result), query)}
                      </div>
                    </div>
                  </div>
                </ListItem>
              </Fade>
            ))}
          </List>
        </>
      );
    }
    
    if (query.trim()) {
      return (
        <div className={classes.noResults}>
          {isQueryArabic ? "لم يتم العثور على نتائج" : "No results found"}
        </div>
      );
    }
    
    return null;
  };

  // Mobile: Full page overlay
  if (isMobile && isOpen) {
    return (
      <Fade in>
        <div className={classes.mobileOverlay}>
          <div className={classes.mobileHeader}>
            <div className={classes.mobileInputWrapper}>
              <SearchIcon style={{ color: "rgba(255,255,255,0.5)", marginRight: 12 }} />
              <TextField
                className={classes.mobileInput}
                placeholder={isQueryArabic || isArabic ? "بحث..." : "Search..."}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                InputProps={{
                  style: { direction: isQueryArabic ? "rtl" : "ltr" },
                }}
              />
            </div>
            <IconButton className={classes.mobileCloseButton} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.mobileResults}>
            {renderResults()}
          </div>
        </div>
      </Fade>
    );
  }

  // Desktop & Mobile collapsed
  return (
    <ClickAwayListener onClickAway={() => !isMobile && handleClose()}>
      <div className={classes.searchContainer}>
        {!isOpen ? (
          <Grow in>
            <IconButton className={classes.searchButton} onClick={() => setIsOpen(true)}>
              <SearchIcon />
            </IconButton>
          </Grow>
        ) : (
          <Grow in>
            <div className={classes.desktopExpanded}>
              <div className={classes.searchInputWrapper}>
                <SearchIcon className={classes.searchIconInput} />
                <TextField
                  className={classes.desktopInput}
                  placeholder={isQueryArabic || isArabic ? "بحث عن شخص..." : "Search..."}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  InputProps={{
                    style: { direction: isQueryArabic ? "rtl" : "ltr" },
                  }}
                />
                <IconButton size="small" className={classes.closeButton} onClick={handleClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
              {(query.trim() || isLoading) && (
                <div className={classes.resultsContainer}>
                  {renderResults()}
                </div>
              )}
            </div>
          </Grow>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default SearchBar;
